import { AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { SortDirection } from '@angular/material/sort';
import { ConsultantSummaryDetails } from 'src/app/models/consultant-summary-details.model';
import { Consultant } from 'src/app/models/consultant.model';
import { Order } from 'src/app/models/order.model';
import { ConsultantService } from 'src/app/services/consultant.service';

type DetailRow = {
  kind: 'single' | 'group' | 'child';
  order: Order;
  amount: number;
  date?: Date | string;
  created?: Date | string;
  children?: DetailRow[];
  expanded?: boolean;
};

@Component({
    selector: 'app-consultant-summary-details',
    templateUrl: './consultant-summary-details.component.html',
    styleUrls: ['./consultant-summary-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConsultantSummaryDetailsComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() details: ConsultantSummaryDetails;
  @Input() consultantId: string;
  @Input() year: number;
  @Input() type: "personalRevenue" | "invoicedPersonalRevenue" | "weightedPipeline" | "leads" | "realizedSales";
  @Input() sortDirection: SortDirection = 'asc';

  displayedColumns: string[] = ['taskNumber', 'customerAndTask', 'personalRevenue', 'invoicedPersonalRevenue', 'weightedPipeline', 'leads', 'realizedSales'];
  @ViewChild(MatTable<Order>) table: MatTable<Order>;
  dataSource: MatTableDataSource<DetailRow>;
  rows: DetailRow[] = [];

  constructor(
    protected consultantService: ConsultantService
  ) {

  }

  async ngOnInit(): Promise<void> {
  }

  async ngAfterViewInit(): Promise<void> {
    
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    let orders = [];

    if (this.type == "leads")
      this.details.leads.forEach(_ => {
        let amount = _.projectSumExVat;

        if (amount > 0)
          orders.push({
            order: _,
            type: "Lead",
            amount
          });
      });

    if (this.type == "weightedPipeline")
      this.details.weightedPipeline.forEach(_ => {
        let share = 1;
        const executor = _.executors.find(_ => _.consultantId == this.consultantId);
        if (executor != null) {
          share = executor.amount / _.pipelineProjectSumExVat;
        }
        let amount = _.pipelineProjectSumExVat * (_.pipelinePercentForSuccess / 100) * share;
        if (amount > 0)
          orders.push({
            order: _,
            type: "Vægtet pipeline",
            amount
          });
      });

    if (this.type == "personalRevenue")
      this.details.personalRevenue.forEach(_ => {
        let amount = _.executors.find(_ => _.consultantId == this.consultantId)?.amount;
        if (amount > 0)
          orders.push({
            order: _,
            type: "Personlig omsætning",
            amount
          });
      });

    if (this.type == "invoicedPersonalRevenue")
      this.details.invoicedPersonalRevenue.forEach(_ => {
        let amount = _.distribution.find(_ => _.consultantId == this.consultantId)?.amount;
        // Credit notes have negative amounts and are counted in the column total,
        // so they belong in the list too.
        if (amount)
          orders.push({
            order: _.order,
            type: "Faktureret Personlig omsætning",
            amount,
            date: _.date,
            created: _.created
          });
      });

    if (this.type == "realizedSales")
      this.details.realizedSales.forEach(_ => {
        let amount = _.salesRealizators.find(_ => _.consultantId == this.consultantId)?.amount;
        if (amount > 0)
          orders.push({
            order: _,
            type: "Real. salg",
            amount
          });
      });

    const collator = new Intl.Collator('da', { numeric: true, sensitivity: 'base' });
    const byTaskNumber = (a: DetailRow, b: DetailRow) => {
      const cmp = collator.compare(a.order?.taskNumber ?? '', b.order?.taskNumber ?? '');
      return this.sortDirection === 'desc' ? -cmp : cmp;
    };
    const time = (d?: Date | string) => d ? new Date(d).getTime() : 0;
    // Oldest first: the invoice date for invoices, otherwise when the order was created.
    const byDate = (a: DetailRow, b: DetailRow) =>
      time(a.date ?? a.order?.created) - time(b.date ?? b.order?.created)
      || time(a.created) - time(b.created);

    // Registrations sharing an Ordre ID are shown as one row with their total,
    // which expands to the individual registrations.
    const byTask = new Map<string, any[]>();
    for (const o of orders) {
      const taskNumber = o.order?.taskNumber;
      if (taskNumber) {
        byTask.set(taskNumber, [...(byTask.get(taskNumber) ?? []), o]);
      }
    }
    const rows: DetailRow[] = [];
    const grouped = new Set<string>();
    for (const o of orders) {
      const taskNumber = o.order?.taskNumber;
      const registrations = byTask.get(taskNumber);
      if (!registrations || registrations.length == 1) {
        rows.push({ ...o, kind: 'single' });
        continue;
      }
      if (grouped.has(taskNumber)) {
        continue;
      }
      grouped.add(taskNumber);
      registrations.sort(byDate);
      rows.push({
        kind: 'group',
        order: registrations[0].order,
        amount: registrations.reduce((sum, r) => sum + r.amount, 0),
        children: registrations.map(r => ({ ...r, kind: 'child' })),
        expanded: false
      });
    }

    this.rows = rows.sort(byTaskNumber);
    this.dataSource = new MatTableDataSource<DetailRow>(this.visibleRows());
  }

  toggle(row: DetailRow) {
    if (row.kind != 'group') {
      return;
    }
    row.expanded = !row.expanded;
    this.dataSource.data = this.visibleRows();
  }

  private visibleRows(): DetailRow[] {
    return this.rows.flatMap(r => r.kind == 'group' && r.expanded ? [r, ...r.children] : [r]);
  }
}
